import React, {Fragment, useCallback, useDeferredValue, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {CenterBox} from "@pg-design/grid-module";
import {h100, mh, mt, ph, positionRelative, w100} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {useIsMounted} from "@pg-mono/hooks";
import type {ICircle, IFitBoundsType, IMarker, IPolygon, IPolygon3D, IPolyline} from "@pg-mono/open-street-map";
import {convertToArrayOfLatLngLiterals, convertToCountryLatLngLiteral, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";

import {osmPublicTileUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {useOfferListMapRailTransportElements} from "../../../offer/hooks/use_offer_list_map_rail_transport_elements";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {Country} from "../../../region/types/Country";
import {ViewType} from "../../../view_type/ViewType";
import {setActivePoi, setActivePoiDirections} from "../../actions/set_poi_travel_directions";
import {useOpenStreetMapAlgolyticsTracking} from "../../hooks/use_open_street_map_algolytics_tracking";
import {useOsmPoiRouteMapElements} from "../../hooks/use_osm_poi_route_map_elements";
import {useOSMPoisMapObjects} from "../../hooks/use_osm_pois_map_objects";
import {useOsmPoisRoutesGrouped} from "../../hooks/use_osm_pois_routes_grouped";
import {usePoiMapObjectsState} from "../../hooks/use_poi_map_objects_state";
import {IPolygonDefinition} from "../../types/IPolygonDefinition";
import {PoiType} from "../../utils/PoiType";
import {MapPlacesModal} from "../MapPlacesModal";
import {OfferInfoWindow} from "../OfferInfoWindow";
import {PoiSwitcher} from "../PoiSwitcher";
import {PoiTravelModeInfoWindow} from "../PoiTravelModeInfoWindow";
import {UserPoi} from "../UserPoi";

const offer_pin = require("../../images/pins/offer_pin.svg");

interface IProps {
    viewType: ViewType;
    offer: IOfferDetail | IOfferModalOffer | IPropertyDetailOffer;
    polygon?: {
        coordinates: number[][][];
    };
    region?: number;
    detailPolygons?: IPolygonDefinition[];
    disablePoiSwitch?: boolean;
    disableInitiallyOpenedPoiId?: boolean;
    drawPoiDistance?: boolean;
    onDistanceChange?: (distance: number) => void;
    showTransportLines?: boolean;
    initialPoiTypes?: PoiType[];
    mobilePoiModalTriggerPosition?: "right" | "center";
    mapConfig?: {
        scrollWheelZoom?: boolean;
        fitBounds?: IFitBoundsType;
        fitBoundsDefaultZoom?: number;
        maxZoom?: number;
    };
    mapBottomSlot?: React.ReactElement | null;
    customPoiMarkers?: IMarker[];
    customMarkers?: IMarker[];
    children?: (props: {
        setCheckedPoiTypes: (checkedPoiTypes: PoiType[]) => void;
        checkedPoiTypes: PoiType[];
        map: React.ReactElement;
        poiDistance: number;
        setPoiDistance: (newDistance: number) => void;
    }) => React.ReactElement;
    clusterMarkers?: boolean;
    className?: string;
    onFullscreenClick?: () => void;
}

export const OpenStreetMapsWithPoi = (props: IProps) => {
    const {
        viewType,
        offer,
        polygon,
        detailPolygons,
        disablePoiSwitch,
        disableInitiallyOpenedPoiId,
        drawPoiDistance,
        onDistanceChange,
        showTransportLines: showTransportLinesProp,
        initialPoiTypes,
        mobilePoiModalTriggerPosition,
        mapConfig,
        mapBottomSlot,
        customPoiMarkers,
        customMarkers,
        children,
        clusterMarkers,
        className,
        onFullscreenClick
    } = props;

    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();
    const theme = useTheme();

    const [showMapPlacesModal, setShowMapPlacesModal] = useState(false);

    const {checkedPoiTypes, onEndPoiEditing, onStartPoiEditing, setCheckedPoiTypes, poiDistance, setPoiDistance} = usePoiMapObjectsState(initialPoiTypes);

    const handleDistanceChange = (newDistance: number) => {
        setPoiDistance(newDistance);
        onDistanceChange?.(newDistance);
    };

    const {userPoiMarkers, poisMarkers, poiDirectionsPolylineCoord} = useOSMPoisMapObjects(offer, {
        disableInitiallyOpenedPoiId,
        checkedPoiTypes,
        radiusInMeters: poiDistance ? poiDistance * 1000 : undefined
    });

    const osmPoisRoutesGrouped = useOsmPoisRoutesGrouped({
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: poiDistance ? poiDistance * 1000 : 3000
    });
    const showTransportLines = Boolean(showTransportLinesProp && checkedPoiTypes.includes(PoiType.TRANSPORT));
    const mapElements = useOfferListMapRailTransportElements({shouldFetch: showTransportLines, routes: [osmPoisRoutesGrouped]});

    const activePoi = useSelector((state: IRPStore) => state.maps.travelDirections.activePoi);
    const activePoiType = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiType);
    const activePoiRoute = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiRoute);

    const osmAlgolyticsTracking = useOpenStreetMapAlgolyticsTracking(viewType);

    const {polyline: activePoiRoutePolyline, markers: activePoiRouteMarkers} = useOsmPoiRouteMapElements(offer.geo_point.coordinates, activePoiRoute);

    const offerMarkerFitBounds = useMemo(
        () => ({
            center: convertToCountryLatLngLiteral(offer.geo_point.coordinates || [], offer.region.country),
            radius: 500
        }),
        [offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], offer.region.country]
    );

    // markers
    const createMarkerList = (): IMarker[] => {
        if (offer) {
            return [
                {
                    id: offer.id,
                    coords: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                    zIndexOffset: 2,
                    icon: {
                        url: offer_pin,
                        sizes: [30, 38]
                    },
                    popup: () => <OfferInfoWindow offer={offer} />,
                    popupShowCloseButton: true,
                    onClick: () => {
                        dispatch(setActivePoi(null, null));
                        dispatch(setActivePoiDirections(null));
                    }
                }
            ];
        }

        return [];
    };

    const markers: IMarker[] = useMemo(() => {
        const createPoisMarkerList = () => {
            const flatPoiList = checkedPoiTypes.reduce((acc, poiType) => {
                return acc.concat(...(poisMarkers[poiType] || []));
            }, [] as IMarker[]);

            const customPoiMarkersList = (customPoiMarkers || []).filter((marker) => marker.poiType && checkedPoiTypes.includes(marker.poiType));

            return [...flatPoiList, ...customPoiMarkersList];
        };

        return [
            ...activePoiRouteMarkers,
            ...createMarkerList(),
            ...createPoisMarkerList(),
            ...userPoiMarkers,
            ...(customMarkers || []),
            ...(mapElements && mapElements.markers && showTransportLines ? mapElements.markers : [])
        ];
    }, [
        activePoiRouteMarkers,
        offer.id,
        offer.geo_point.coordinates[0],
        offer.geo_point.coordinates[1],
        checkedPoiTypes,
        userPoiMarkers,
        customMarkers,
        customPoiMarkers,
        poisMarkers,
        showTransportLines,
        mapElements.markers
    ]);

    // polygons
    const createPolygonList = () => {
        if ("geo_area" in offer && offer.geo_area) {
            return convertToArrayOfLatLngLiterals(offer.geo_area.coordinates, {reversedValues: true});
        }
    };

    const deferredMarkers = useDeferredValue(markers);

    const polygons: IPolygon[] = useMemo(
        () => [
            {
                id: "default-polygon",
                positions: createPolygonList() || [],

                pathOptions: {
                    fillColor: "#FFCDA5",
                    color: "#23232D",
                    fillOpacity: 0.8,
                    weight: 2
                }
            },
            ...(detailPolygons
                ? detailPolygons.map(
                      (detailPolygon): IPolygon3D => ({
                          id: JSON.stringify(detailPolygon.coords),
                          positions: convertToArrayOfLatLngLiterals(detailPolygon.coords, {reversedValues: true}),
                          height: detailPolygon.height || 1,
                          popup: detailPolygon?.infoWindow?.content
                              ? () => (
                                    <Text variant="headline_4" css={[ph()]}>
                                        {`${detailPolygon?.infoWindow?.content}`}
                                    </Text>
                                )
                              : undefined,
                          pathOptions: {
                              fillColor: detailPolygon.options?.fillColor,
                              color: detailPolygon.options?.strokeColor
                          }
                      })
                  )
                : [])
        ],
        [offer.id, offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], polygon, detailPolygons]
    );

    // polylines
    const polylines: IPolyline[] = useMemo(() => {
        const userPolyline: IPolyline = {
            id: "travel",
            positions: poiDirectionsPolylineCoord,
            pathOptions: {
                color: theme.colors.danger,
                weight: 5
            }
        };

        return [
            userPolyline,
            ...(activePoiRoutePolyline ? [activePoiRoutePolyline] : []),
            ...(mapElements && mapElements.polylines && showTransportLines ? mapElements.polylines : [])
        ];
    }, [poiDirectionsPolylineCoord, activePoiRoutePolyline, showTransportLines, mapElements.polylines]);

    const circles: ICircle[] | undefined = useMemo(() => {
        return drawPoiDistance
            ? [
                  {
                      center: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                      radius: poiDistance * 1000,
                      pathOptions: {
                          color: "#EBFF00",
                          opacity: 0.3
                      }
                  }
              ]
            : [];
    }, [drawPoiDistance, offer.geo_point.coordinates, offer.region.country, poiDistance]);

    const onShowPoiSwitcherModal = () => {
        onStartPoiEditing();
        setShowMapPlacesModal(true);
    };

    const onClosePoiSwitcherModal = (cancelChanges: boolean) => {
        if (cancelChanges) {
            onEndPoiEditing();
        }

        setShowMapPlacesModal(false);
    };

    const isMarkerPopupDisabled = useCallback((marker: IMarker) => isMobile === true && marker.id !== offer.id, [isMobile, offer.id]);

    const map = useMemo(() => {
        return (
            <>
                <LazyOpenStreetMap
                    scrollWheelZoom={mapConfig?.scrollWheelZoom ?? true}
                    fitBounds={mapConfig?.fitBounds}
                    {...osmAlgolyticsTracking}
                    minFitBounds={offerMarkerFitBounds}
                    polygons={polygons}
                    markers={deferredMarkers}
                    polylines={polylines}
                    markerShowPopupOnHover={false}
                    markerDisablePopup={isMarkerPopupDisabled}
                    maxZoom={mapConfig?.maxZoom || 16}
                    clusterMarkers={clusterMarkers}
                    tileUrl={offer.region.country === Country.SPAIN ? osmPublicTileUrl : null}
                    fitBoundsDefaultZoom={mapConfig?.fitBoundsDefaultZoom}
                    circles={circles}
                    onMarkerInvalidCoords={(marker) => {
                        const message = "Invalid marker coords in OpenStreetMapsWithPoi";

                        notifyBugsnagClient(new Error(message), message, JSON.stringify(marker));
                    }}
                    className={className}
                    onFullscreenClick={onFullscreenClick}
                />
            </>
        );
    }, [mapConfig?.scrollWheelZoom, osmAlgolyticsTracking.onMapMove, osmAlgolyticsTracking.onMapInit, polygons, polylines, deferredMarkers]);

    const defaultRenderer = () => (
        <>
            {disablePoiSwitch ? null : (
                <div css={mapPlacesPosition}>
                    <PoiSwitcher
                        onChange={setCheckedPoiTypes}
                        checkedPoiTypes={checkedPoiTypes}
                        distanceValue={poiDistance}
                        onDistanceChange={handleDistanceChange}
                    />

                    <div css={[mt(2)]}>
                        <UserPoi offer={offer} />
                    </div>
                </div>
            )}

            {map}
        </>
    );

    const isMounted = useIsMounted();

    if (isMounted && offer) {
        return (
            <div css={[w100, h100, positionRelative]}>
                {disablePoiSwitch ? null : (
                    <FiltersIcon
                        wrapperSize="4"
                        size="2.4"
                        wrapperColor={theme.colors.primary}
                        onClick={onShowPoiSwitcherModal}
                        css={[mapPlacesButton, mapPlacesButtonPosition(mobilePoiModalTriggerPosition)]}
                    />
                )}

                {children
                    ? children({
                          setCheckedPoiTypes,
                          checkedPoiTypes,
                          poiDistance,
                          setPoiDistance: handleDistanceChange,
                          map
                      })
                    : defaultRenderer()}

                {disablePoiSwitch ? null : (
                    <MapPlacesModal modalState={showMapPlacesModal} onModalClose={onClosePoiSwitcherModal}>
                        <Fragment>
                            <PoiSwitcher
                                onChange={setCheckedPoiTypes}
                                checkedPoiTypes={checkedPoiTypes}
                                distanceValue={poiDistance}
                                onDistanceChange={handleDistanceChange}
                            />
                            <UserPoi offer={offer} />
                        </Fragment>
                    </MapPlacesModal>
                )}

                <div css={poiTravelModeInfoWindowMobile} id="poiTravelModeInfoWindowMobile">
                    {isMobile && activePoi && activePoiType ? (
                        <PoiTravelModeInfoWindow calcTravelDataOnOpen poi={activePoi} poiType={activePoiType} targetCoords={offer.geo_point.coordinates} />
                    ) : null}
                </div>

                {mapBottomSlot ? <div css={mapBottomSlotStyle}>{mapBottomSlot}</div> : null}
            </div>
        );
    }

    return (
        <CenterBox>
            <Loader size="lg" />
        </CenterBox>
    );
};

const buttonsZIndex = 10;

const mapPlacesPosition = (theme: Theme) => css`
    position: absolute;
    top: 2rem;
    left: 2.5rem;
    z-index: ${buttonsZIndex};

    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const mapPlacesButton = (theme: Theme) => css`
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
    z-index: ${buttonsZIndex};
    cursor: pointer;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const mapPlacesButtonPosition = (position: IProps["mobilePoiModalTriggerPosition"]) => css`
    ${position === "center"
        ? css`
              right: 0;
              left: 0;
              ${mh("auto")};
          `
        : ""};
`;

const poiTravelModeInfoWindowMobile = (theme: Theme) => css`
    position: absolute;
    bottom: 1.6rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    width: calc(100% - 3.2rem);
    pointer-events: auto;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const mapBottomSlotStyle = css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;
import React, {Fragment, useCallback, useDeferredValue, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {CenterBox} from "@pg-design/grid-module";
import {h100, mh, mt, ph, positionRelative, w100} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {useIsMounted} from "@pg-mono/hooks";
import type {ICircle, IFitBoundsType, IMarker, IPolygon, IPolygon3D, IPolyline} from "@pg-mono/open-street-map";
import {convertToArrayOfLatLngLiterals, convertToCountryLatLngLiteral, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";

import {osmPublicTileUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {useOfferListMapRailTransportElements} from "../../../offer/hooks/use_offer_list_map_rail_transport_elements";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {Country} from "../../../region/types/Country";
import {ViewType} from "../../../view_type/ViewType";
import {setActivePoi, setActivePoiDirections} from "../../actions/set_poi_travel_directions";
import {useOpenStreetMapAlgolyticsTracking} from "../../hooks/use_open_street_map_algolytics_tracking";
import {useOsmPoiRouteMapElements} from "../../hooks/use_osm_poi_route_map_elements";
import {useOSMPoisMapObjects} from "../../hooks/use_osm_pois_map_objects";
import {useOsmPoisRoutesGrouped} from "../../hooks/use_osm_pois_routes_grouped";
import {usePoiMapObjectsState} from "../../hooks/use_poi_map_objects_state";
import {IPolygonDefinition} from "../../types/IPolygonDefinition";
import {PoiType} from "../../utils/PoiType";
import {MapPlacesModal} from "../MapPlacesModal";
import {OfferInfoWindow} from "../OfferInfoWindow";
import {PoiSwitcher} from "../PoiSwitcher";
import {PoiTravelModeInfoWindow} from "../PoiTravelModeInfoWindow";
import {UserPoi} from "../UserPoi";

const offer_pin = require("../../images/pins/offer_pin.svg");

interface IProps {
    viewType: ViewType;
    offer: IOfferDetail | IOfferModalOffer | IPropertyDetailOffer;
    polygon?: {
        coordinates: number[][][];
    };
    region?: number;
    detailPolygons?: IPolygonDefinition[];
    disablePoiSwitch?: boolean;
    disableInitiallyOpenedPoiId?: boolean;
    drawPoiDistance?: boolean;
    onDistanceChange?: (distance: number) => void;
    showTransportLines?: boolean;
    initialPoiTypes?: PoiType[];
    mobilePoiModalTriggerPosition?: "right" | "center";
    mapConfig?: {
        scrollWheelZoom?: boolean;
        fitBounds?: IFitBoundsType;
        fitBoundsDefaultZoom?: number;
        maxZoom?: number;
    };
    mapBottomSlot?: React.ReactElement | null;
    customPoiMarkers?: IMarker[];
    customMarkers?: IMarker[];
    children?: (props: {
        setCheckedPoiTypes: (checkedPoiTypes: PoiType[]) => void;
        checkedPoiTypes: PoiType[];
        map: React.ReactElement;
        poiDistance: number;
        setPoiDistance: (newDistance: number) => void;
    }) => React.ReactElement;
    clusterMarkers?: boolean;
    className?: string;
    onFullscreenClick?: () => void;
}

export const OpenStreetMapsWithPoi = (props: IProps) => {
    const {
        viewType,
        offer,
        polygon,
        detailPolygons,
        disablePoiSwitch,
        disableInitiallyOpenedPoiId,
        drawPoiDistance,
        onDistanceChange,
        showTransportLines: showTransportLinesProp,
        initialPoiTypes,
        mobilePoiModalTriggerPosition,
        mapConfig,
        mapBottomSlot,
        customPoiMarkers,
        customMarkers,
        children,
        clusterMarkers,
        className,
        onFullscreenClick
    } = props;

    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();
    const theme = useTheme();

    const [showMapPlacesModal, setShowMapPlacesModal] = useState(false);

    const {checkedPoiTypes, onEndPoiEditing, onStartPoiEditing, setCheckedPoiTypes, poiDistance, setPoiDistance} = usePoiMapObjectsState(initialPoiTypes);

    const handleDistanceChange = (newDistance: number) => {
        setPoiDistance(newDistance);
        onDistanceChange?.(newDistance);
    };

    const {userPoiMarkers, poisMarkers, poiDirectionsPolylineCoord} = useOSMPoisMapObjects(offer, {
        disableInitiallyOpenedPoiId,
        checkedPoiTypes,
        radiusInMeters: poiDistance ? poiDistance * 1000 : undefined
    });

    const osmPoisRoutesGrouped = useOsmPoisRoutesGrouped({
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: poiDistance ? poiDistance * 1000 : 3000
    });
    const showTransportLines = Boolean(showTransportLinesProp && checkedPoiTypes.includes(PoiType.TRANSPORT));
    const mapElements = useOfferListMapRailTransportElements({shouldFetch: showTransportLines, routes: [osmPoisRoutesGrouped]});

    const activePoi = useSelector((state: IRPStore) => state.maps.travelDirections.activePoi);
    const activePoiType = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiType);
    const activePoiRoute = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiRoute);

    const osmAlgolyticsTracking = useOpenStreetMapAlgolyticsTracking(viewType);

    const {polyline: activePoiRoutePolyline, markers: activePoiRouteMarkers} = useOsmPoiRouteMapElements(offer.geo_point.coordinates, activePoiRoute);

    const offerMarkerFitBounds = useMemo(
        () => ({
            center: convertToCountryLatLngLiteral(offer.geo_point.coordinates || [], offer.region.country),
            radius: 500
        }),
        [offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], offer.region.country]
    );

    // markers
    const createMarkerList = (): IMarker[] => {
        if (offer) {
            return [
                {
                    id: offer.id,
                    coords: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                    zIndexOffset: 2,
                    icon: {
                        url: offer_pin,
                        sizes: [30, 38]
                    },
                    popup: () => <OfferInfoWindow offer={offer} />,
                    popupShowCloseButton: true,
                    onClick: () => {
                        dispatch(setActivePoi(null, null));
                        dispatch(setActivePoiDirections(null));
                    }
                }
            ];
        }

        return [];
    };

    const markers: IMarker[] = useMemo(() => {
        const createPoisMarkerList = () => {
            const flatPoiList = checkedPoiTypes.reduce((acc, poiType) => {
                return acc.concat(...(poisMarkers[poiType] || []));
            }, [] as IMarker[]);

            const customPoiMarkersList = (customPoiMarkers || []).filter((marker) => marker.poiType && checkedPoiTypes.includes(marker.poiType));

            return [...flatPoiList, ...customPoiMarkersList];
        };

        return [
            ...activePoiRouteMarkers,
            ...createMarkerList(),
            ...createPoisMarkerList(),
            ...userPoiMarkers,
            ...(customMarkers || []),
            ...(mapElements && mapElements.markers && showTransportLines ? mapElements.markers : [])
        ];
    }, [
        activePoiRouteMarkers,
        offer.id,
        offer.geo_point.coordinates[0],
        offer.geo_point.coordinates[1],
        checkedPoiTypes,
        userPoiMarkers,
        customMarkers,
        customPoiMarkers,
        poisMarkers,
        showTransportLines,
        mapElements.markers
    ]);

    // polygons
    const createPolygonList = () => {
        if ("geo_area" in offer && offer.geo_area) {
            return convertToArrayOfLatLngLiterals(offer.geo_area.coordinates, {reversedValues: true});
        }
    };

    const deferredMarkers = useDeferredValue(markers);

    const polygons: IPolygon[] = useMemo(
        () => [
            {
                id: "default-polygon",
                positions: createPolygonList() || [],

                pathOptions: {
                    fillColor: "#FFCDA5",
                    color: "#23232D",
                    fillOpacity: 0.8,
                    weight: 2
                }
            },
            ...(detailPolygons
                ? detailPolygons.map(
                      (detailPolygon): IPolygon3D => ({
                          id: JSON.stringify(detailPolygon.coords),
                          positions: convertToArrayOfLatLngLiterals(detailPolygon.coords, {reversedValues: true}),
                          height: detailPolygon.height || 1,
                          popup: detailPolygon?.infoWindow?.content
                              ? () => (
                                    <Text variant="headline_4" css={[ph()]}>
                                        {`${detailPolygon?.infoWindow?.content}`}
                                    </Text>
                                )
                              : undefined,
                          pathOptions: {
                              fillColor: detailPolygon.options?.fillColor,
                              color: detailPolygon.options?.strokeColor
                          }
                      })
                  )
                : [])
        ],
        [offer.id, offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], polygon, detailPolygons]
    );

    // polylines
    const polylines: IPolyline[] = useMemo(() => {
        const userPolyline: IPolyline = {
            id: "travel",
            positions: poiDirectionsPolylineCoord,
            pathOptions: {
                color: theme.colors.danger,
                weight: 5
            }
        };

        return [
            userPolyline,
            ...(activePoiRoutePolyline ? [activePoiRoutePolyline] : []),
            ...(mapElements && mapElements.polylines && showTransportLines ? mapElements.polylines : [])
        ];
    }, [poiDirectionsPolylineCoord, activePoiRoutePolyline, showTransportLines, mapElements.polylines]);

    const circles: ICircle[] | undefined = useMemo(() => {
        return drawPoiDistance
            ? [
                  {
                      center: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                      radius: poiDistance * 1000,
                      pathOptions: {
                          color: "#EBFF00",
                          opacity: 0.3
                      }
                  }
              ]
            : [];
    }, [drawPoiDistance, offer.geo_point.coordinates, offer.region.country, poiDistance]);

    const onShowPoiSwitcherModal = () => {
        onStartPoiEditing();
        setShowMapPlacesModal(true);
    };

    const onClosePoiSwitcherModal = (cancelChanges: boolean) => {
        if (cancelChanges) {
            onEndPoiEditing();
        }

        setShowMapPlacesModal(false);
    };

    const isMarkerPopupDisabled = useCallback((marker: IMarker) => isMobile === true && marker.id !== offer.id, [isMobile, offer.id]);

    const map = useMemo(() => {
        return (
            <>
                <LazyOpenStreetMap
                    scrollWheelZoom={mapConfig?.scrollWheelZoom ?? true}
                    fitBounds={mapConfig?.fitBounds}
                    {...osmAlgolyticsTracking}
                    minFitBounds={offerMarkerFitBounds}
                    polygons={polygons}
                    markers={deferredMarkers}
                    polylines={polylines}
                    markerShowPopupOnHover={false}
                    markerDisablePopup={isMarkerPopupDisabled}
                    maxZoom={mapConfig?.maxZoom || 16}
                    clusterMarkers={clusterMarkers}
                    tileUrl={offer.region.country === Country.SPAIN ? osmPublicTileUrl : null}
                    fitBoundsDefaultZoom={mapConfig?.fitBoundsDefaultZoom}
                    circles={circles}
                    onMarkerInvalidCoords={(marker) => {
                        const message = "Invalid marker coords in OpenStreetMapsWithPoi";

                        notifyBugsnagClient(new Error(message), message, JSON.stringify(marker));
                    }}
                    className={className}
                    onFullscreenClick={onFullscreenClick}
                />
            </>
        );
    }, [mapConfig?.scrollWheelZoom, osmAlgolyticsTracking.onMapMove, osmAlgolyticsTracking.onMapInit, polygons, polylines, deferredMarkers]);

    const defaultRenderer = () => (
        <>
            {disablePoiSwitch ? null : (
                <div css={mapPlacesPosition}>
                    <PoiSwitcher
                        onChange={setCheckedPoiTypes}
                        checkedPoiTypes={checkedPoiTypes}
                        distanceValue={poiDistance}
                        onDistanceChange={handleDistanceChange}
                    />

                    <div css={[mt(2)]}>
                        <UserPoi offer={offer} />
                    </div>
                </div>
            )}

            {map}
        </>
    );

    const isMounted = useIsMounted();

    if (isMounted && offer) {
        return (
            <div css={[w100, h100, positionRelative]}>
                {disablePoiSwitch ? null : (
                    <FiltersIcon
                        wrapperSize="4"
                        size="2.4"
                        wrapperColor={theme.colors.primary}
                        onClick={onShowPoiSwitcherModal}
                        css={[mapPlacesButton, mapPlacesButtonPosition(mobilePoiModalTriggerPosition)]}
                    />
                )}

                {children
                    ? children({
                          setCheckedPoiTypes,
                          checkedPoiTypes,
                          poiDistance,
                          setPoiDistance: handleDistanceChange,
                          map
                      })
                    : defaultRenderer()}

                {disablePoiSwitch ? null : (
                    <MapPlacesModal modalState={showMapPlacesModal} onModalClose={onClosePoiSwitcherModal}>
                        <Fragment>
                            <PoiSwitcher
                                onChange={setCheckedPoiTypes}
                                checkedPoiTypes={checkedPoiTypes}
                                distanceValue={poiDistance}
                                onDistanceChange={handleDistanceChange}
                            />
                            <UserPoi offer={offer} />
                        </Fragment>
                    </MapPlacesModal>
                )}

                <div css={poiTravelModeInfoWindowMobile} id="poiTravelModeInfoWindowMobile">
                    {isMobile && activePoi && activePoiType ? (
                        <PoiTravelModeInfoWindow calcTravelDataOnOpen poi={activePoi} poiType={activePoiType} targetCoords={offer.geo_point.coordinates} />
                    ) : null}
                </div>

                {mapBottomSlot ? <div css={mapBottomSlotStyle}>{mapBottomSlot}</div> : null}
            </div>
        );
    }

    return (
        <CenterBox>
            <Loader size="lg" />
        </CenterBox>
    );
};

const buttonsZIndex = 10;

const mapPlacesPosition = (theme: Theme) => css`
    position: absolute;
    top: 2rem;
    left: 2.5rem;
    z-index: ${buttonsZIndex};

    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const mapPlacesButton = (theme: Theme) => css`
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
    z-index: ${buttonsZIndex};
    cursor: pointer;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const mapPlacesButtonPosition = (position: IProps["mobilePoiModalTriggerPosition"]) => css`
    ${position === "center"
        ? css`
              right: 0;
              left: 0;
              ${mh("auto")};
          `
        : ""};
`;

const poiTravelModeInfoWindowMobile = (theme: Theme) => css`
    position: absolute;
    bottom: 1.6rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    width: calc(100% - 3.2rem);
    pointer-events: auto;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const mapBottomSlotStyle = css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;
